<script setup lang="ts">
import {ChevronSelectorVertical, Inbox01} from "utruckin-icons";
import {computed} from "vue";
import {ITableEmits, ITableHeader, ITableProps} from "@/types/table";
import CustomLoading from "@/components/custom/CustomLoading.vue";
import {useStatsStore} from "@/features/stats/stats.store";

const props = withDefaults(defineProps<ITableProps>(), {
  loading: false,
  headers: undefined,
  items: undefined,
  striped: true,
});

const emit = defineEmits<ITableEmits>()

const store = useStatsStore();

const rowClick = (row: any, rowIndex: number, $event: MouseEvent) => {
  emit("row-clicked", row, rowIndex, $event);
};

const checkDisabled = computed(() => {
  return (item: ITableHeader): boolean => {
    if (item.panel && item.panel.length) {
      for (let index = 0; index < item.panel.length; index++) {
        if (store.panels[item.panel[index]]) {
          return true;
        }
      }

      return false;
    }
    return true
  }
})

const getColumnStyle = (header: ITableHeader) => {
  if (header.fitContent) {
    return {
      width: '1%',
      whiteSpace: 'nowrap'
    }
  }

  if (header.width) {
    return {
      width: header.width
    }
  }

  return {
    width: 'auto'
  }
}
</script>

<template>
  <div class="c-co-table">
    <template v-if="!loading">
      <table class="table" :class="{'striped': striped}">
        <colgroup>
          <col
              v-for="(header, index) in headers"
              :key="index"
              :style="getColumnStyle(header)"
          />
        </colgroup>
        <thead>
        <tr>
          <th v-for="(item, index) in headers" :key="index">
            <div class="table__head">
              <span class="table__head--label">{{ item.label }}</span>
              <div class="table__head--action">
                <ChevronSelectorVertical :size="12"/>
              </div>
            </div>
          </th>
        </tr>
        </thead>
        <tbody>
        <template v-if="items?.length">
          <tr
              v-for="(item, rowIdx) in items"
              :key="rowIdx"
              @click="(e) => rowClick(item, rowIdx, e)"
          >
            <td v-for="(cellItem, cellIdx) in headers" :key="cellIdx"
                :class="[
                checkDisabled(cellItem) ? '' : 'disabled',
                cellItem.type == 'error' ? 'error' : ''
                ]">
              <slot :name="cellItem.name" :item="item" :index="rowIdx">
                {{ item[cellItem.name] }}
              </slot>
            </td>
          </tr>
        </template>
        <template v-else>
          <tr>
            <td class="no__data" :colspan="headers?.length">
              <div class="no__data--body">
                <Inbox01 :size="40"/>
                Data not found
              </div>
            </td>
          </tr>
        </template>
        </tbody>
      </table>
    </template>
    <template v-else>
      <CustomLoading v-for="n in 7" height="54px" width="100%" :key="n" style="margin-bottom: 8px"/>
    </template>
  </div>
</template>

<style lang="scss">
.c-co-table {
  width: 100%;
  margin-top: 16px;
  max-height: calc(100vh - 357px);
  overflow: auto;

  table {
    height: 100%;
    width: 100%;
    table-layout: auto;
    border-bottom: 1px solid var(--color-border);
    border-left: 1px solid var(--color-border);
    border-right: 1px solid var(--color-border);
    border-radius: 16px;
    border-spacing: 0;

    &.striped tbody tr:nth-child(odd) {
      background-color: var(--color-table-row);
    }

    thead {
      width: 100%;
      border-radius: 16px;
      border: 1px solid var(--color-border);
      position: sticky;
      z-index: 5;
      top: 0;

      th {
        padding: 10px 12px;
        font-size: 14px;
        font-weight: 600;
        line-height: 20px;
        border-bottom: 1px solid var(--color-border);
        border-top: 1px solid var(--color-border);
        text-align: left;
        color: var(--color-secondary-label);
        background: var(--color-background);

        &:first-child {
          border-bottom-left-radius: 16px;
          border-top-left-radius: 16px;
        }

        &:last-child {
          border-bottom-right-radius: 16px;
          border-top-right-radius: 16px;
        }

        .table__head {
          display: flex;
          justify-content: start;
          align-items: center;
          gap: 4px;

          &--action {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: start;
          }
        }
      }
    }

    tbody {
      tr {
        cursor: pointer;

        &:hover {
          background: var(--color-bg-secondary);
        }

        td {
          padding: 10px 12px;
          color: var(--color-label);
          font-size: 14px;
          line-height: 20px;
          font-weight: 500;

          &.error {
            color: var(--color-red);
          }

          &.disabled {
            color: var(--color-label-disabled);
          }

          .no__data {
            &--body {
              display: flex;
              flex-direction: column;
              width: 100%;
              justify-content: center;
              align-items: center;
              font-size: 18px;
              line-height: 24px;
            }
          }

        }

        &:not(:last-child) td {
          border-bottom: 1px solid var(--color-border);
        }
      }
    }
  }
}
</style>
